import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import {
  Alert,
  Alerts,
  Button,
  ButtonAction,
  Col,
  Container,
  FormNotice,
  Header,
  PageContent,
  Row,
  Spinner,
  Table,
  StatusBadge,
} from 'components';
import { checkPermissions, isLoading, globalPropTypes } from 'helpers';
import { formatDate, formatDateTime } from 'helpers/date';
import { loadYcReportsCreator } from 'store/actionsCreators';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.yc.reports',
    url: '',
  },
];

/**
 * The yearly reports of one Young Crew.
 *
 * The same screen carries the history - every report of the years before - and
 * the one of the year that is being reported on right now, which is opened for
 * reading or for reworking depending on where it stands.
 */
const Reports = ({
  items,
  schedule,
  canAddReport,
  currentReportId,
  loading,
  userPermissions,
  loadReports,
}) => {
  const { t } = useTranslation();

  const allowed = checkPermissions({ roles: ['yc_reports_view'] }, userPermissions || []);

  useEffect(() => {
    if (allowed) loadReports();
  }, [allowed]);

  if (!allowed) {
    return (
      <>
        <Header breadcrumbs={breadcrumbs} title={t('page.yc.reports.title')} />
        <PageContent>
          <Container>
            <Alert type="info">{t('page.forbidden.subtitle')}</Alert>
          </Container>
        </PageContent>
      </>
    );
  }

  const renderRows = () => {
    if (loading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={5}>
            <Spinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (!items.length) {
      return (
        <Table.Row>
          <Table.Cell colSpan={5}>{t('global.noResultsFound')}</Table.Cell>
        </Table.Row>
      );
    }

    return items.map((item) => (
      <Table.Row key={item.id}>
        <Table.Cell>
          <Table.Label>{t('page.yc.reports.year')}:</Table.Label>
          {item.year}
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.yc.reports.sentOn')}:</Table.Label>
          {item.submittedAt ? formatDateTime(item.submittedAt) : '-'}
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.yc.reports.events')}:</Table.Label>
          {item.eventsCount}
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.yc.reports.status')}:</Table.Label>
          <StatusBadge status={item.status}>{item.statusLabel}</StatusBadge>
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.yc.reports.action')}:</Table.Label>
          <ButtonAction
            as={Link}
            to={`/yc/report/${item.id}`}
            title={t('global.readButton')}
            style={{ alignItems: 'baseline', marginLeft: '0' }}
          />
          {item.editable ? (
            <ButtonAction
              as={Link}
              to={`/yc/report/${item.id}/edit`}
              title={t('global.editButton')}
              style={{ alignItems: 'baseline' }}
            />
          ) : null}
        </Table.Cell>
      </Table.Row>
    ));
  };

  // Either the report of the open window can be started, or it was started
  // already and is picked up where it was left - never both.
  const renderAddAction = () => {
    if (canAddReport) {
      return (
        <Button as={Link} to="/yc/report/add">
          {t('page.yc.reports.addTheReport')}
        </Button>
      );
    }

    if (schedule && currentReportId) {
      return (
        <Button as={Link} to={`/yc/report/${currentReportId}/edit`}>
          {t('page.yc.reports.continueTheReport')}
        </Button>
      );
    }

    return <Button disabled>{t('page.yc.reports.addTheReport')}</Button>;
  };

  return (
    <>
      <Header breadcrumbs={breadcrumbs} title={t('page.yc.reports.title')} />
      <PageContent>
        <Container>
          <Alerts type="yc" />
          <Col>
            {schedule ? (
              <FormNotice type="info">
                {t('page.yc.reports.openPeriod', {
                  year: schedule.year,
                  date: formatDate(schedule.reportEnd),
                })}
              </FormNotice>
            ) : (
              <FormNotice type="empty">{t('page.yc.reports.closedPeriod')}</FormNotice>
            )}

            <Margin bottom={{ xs: 32 }}>
              <Table tablebreakpoint="lg">
                <Table.Head>
                  <Table.Row>
                    <Table.Heading>{t('page.yc.reports.year')}</Table.Heading>
                    <Table.Heading>{t('page.yc.reports.sentOn')}</Table.Heading>
                    <Table.Heading>{t('page.yc.reports.events')}</Table.Heading>
                    <Table.Heading>{t('page.yc.reports.status')}</Table.Heading>
                    <Table.Heading>{t('page.yc.reports.action')}</Table.Heading>
                  </Table.Row>
                </Table.Head>
                <Table.Body>{renderRows()}</Table.Body>
              </Table>
            </Margin>

            {checkPermissions({ roles: ['yc_reports_send'] }, userPermissions || []) ? (
              <Row>
                <Margin as={Col} size={{ xl: 1 / 5 }}>
                  {renderAddAction()}
                </Margin>
              </Row>
            ) : null}
          </Col>
        </Container>
      </PageContent>
    </>
  );
};

Reports.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      year: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      eventsCount: PropTypes.number,
      submittedAt: PropTypes.string,
      editable: PropTypes.bool,
    }),
  ),
  schedule: PropTypes.shape({
    year: PropTypes.number,
    reportEnd: PropTypes.string,
  }),
  canAddReport: PropTypes.bool,
  currentReportId: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loadReports: PropTypes.func.isRequired,
};

Reports.defaultProps = {
  items: [],
  schedule: null,
  canAddReport: false,
  currentReportId: null,
};

const mapStateToProps = ({ yc, user, loading }) => ({
  items: yc.reports.items,
  schedule: yc.reports.schedule,
  canAddReport: yc.reports.canAddReport,
  currentReportId: yc.reports.currentReportId,
  loading: isLoading(loading, 'GLOBAL'),
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadReports: () => dispatch(loadYcReportsCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Reports);
